import TableC from "@/CommonElements/TableC";
import BtnAsLink from "@/Component/BtnAsLink";
import { useDeleteExaminationPeriodMutation } from "@/services/basicData";
import { useExaminationPeriodsQuery } from "@/services/basicData";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useSystem } from "@/utils/hooks/useLocation";
import {
  AddSquareIcon,
  DeleteIcon,
  Edit02Icon,
} from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { Tag } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

export const ExaminationPeriodsList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );
  const { t } = useTranslation();
  const { confirm, ModalComponent } = useConfirm();
  const { currentSystem } = useSystem();
  // ==============
  //   Side Effects
  // ==============
  const {
    mutate: deleteExaminationPeriod,
  } = useDeleteExaminationPeriodMutation();
  const { data: examinationPeriodsRes, isLoading } =
    useExaminationPeriodsQuery({
      params: {
        page: currentPage,
        per_page: 10,
        query: debouncedValue,
      },
    });
  const examinationPeriods = examinationPeriodsRes?.data?.data ?? [];
  const meta = examinationPeriodsRes?.data?.meta ?? {};

  const columns = [
    {
      name: t("NAME_AR"),
      key: "name_ar",
    },
    {
      name: t("NAME_EN"),
      key: "name_en",
    },
    {
      name: t("TIME_FROM"),
      key: "time_from",
    },
    {
      name: t("TIME_TO"),
      key: "time_to",
    },
    {
      name: t("STATUS"),
      key: "is_active",
      render: (value, rowData) => (
        <Tag color={value ? "success" : "error"}>
          {value ? t("ACTIVE") : t("INACTIVE")}
        </Tag>
      ),
    },
    {
      name: t("ACTIONS"),
      key: "actions",
      filterable: false,
    },
  ];

  // =========
  // functions
  // =========
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteClick = (id) => {
    confirm({
      title: t("CONFIRM_DELETE_EXAMINATION_PERIOD_TITLE"),
      content: t("CONFIRM_DELETE_EXAMINATION_PERIOD_MESSAGE"),
      okText: t("CONFIRM"),
      cancelText: t("CANCEL"),
      onOk: ({ closeModal }) => {
        deleteExaminationPeriod(
          { id },
          {
            onSuccess: () => {
              closeModal();
            },
          }
        );

        return false;
      },
      onCancel: () => {},
    });
  };

  // Define actions function
  const getActions = (rowData) => {
    return [
      {
        key: "edit",
        label: (
          <Link
            to={`/app/${currentSystem}/basic-data/examination_periods/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      },
    ];
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link={`/app/${currentSystem}/basic-data/examination_periods/create`}
    >
      {t("CREATE_EXAMINATION_PERIOD")}
    </BtnAsLink>
  );

  return (
    <div>
      <TableC
        data={examinationPeriods}
        // {...restClientSearch}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        columns={columns}
        loading={isLoading}
        actions={getActions}
        renderActions={createButton}
        // emptyState={EmptyState}
        // minVisibleColumns={2}
        skeletonCount={10}
        pagination={{
          current: currentPage,
          total: meta?.total ?? 0,
          pageSize: meta?.per_page ?? 10,
          onChange: handlePageChange,
        }}
      />
      <ModalComponent />
    </div>
  );
};
